/**
 * Mutaciones local-first de COHORTES y PROGRAMAS (F2-A).
 *
 * Escriben en PowerSync → write-path (`db/sync.rs`), que aplica la authz
 * (ManageAcademics con scoping de sede, o profesor lead) y las reglas de
 * matrícula server-authoritative (transiciones, cupo, waitlist). La ACTIVACIÓN
 * y la promoción de waitlist tienen además endpoints REST inmediatos
 * (/api/cohorts/...) para los flujos de aprobación.
 */

import {
  cohort,
  cohort_enrollment,
  cohort_group,
  cohort_group_member,
  cohort_module_setting,
  cohort_teacher,
  eq,
  type PowerSyncDrizzleDB,
  program,
  program_course,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";
import { DEFAULT_COHORT_MODALITY } from "../verticals";
import { DEFAULT_PROGRAM_KIND, type ProgramKind } from "../programs/kinds";

// ─── Programas ───────────────────────────────────────────────────────────────

export async function createProgramMutation(
  db: PowerSyncDrizzleDB,
  params: {
    organizationId: string;
    name?: string;
    description?: string;
    kind?: ProgramKind;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(program).values({
    id,
    organization_id: params.organizationId,
    name: params.name?.trim() || "Nuevo programa",
    description: params.description ?? null,
    kind: params.kind ?? DEFAULT_PROGRAM_KIND,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateProgramMutation(
  db: PowerSyncDrizzleDB,
  programId: string,
  updates: Partial<{ name: string; description: string | null; kind: string }>,
): Promise<void> {
  await db
    .update(program)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(program.id, programId));
}

export async function deleteProgramMutation(
  db: PowerSyncDrizzleDB,
  programId: string,
): Promise<void> {
  await db
    .update(program)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(program.id, programId));
}

export async function addProgramCourseMutation(
  db: PowerSyncDrizzleDB,
  params: {
    programId: string;
    organizationId: string;
    courseId: string;
    position?: number;
    required?: boolean;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(program_course).values({
    id,
    program_id: params.programId,
    organization_id: params.organizationId,
    course_id: params.courseId,
    position: params.position ?? 0,
    required: params.required === false ? 0 : 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function removeProgramCourseMutation(
  db: PowerSyncDrizzleDB,
  programCourseId: string,
): Promise<void> {
  await db.delete(program_course).where(eq(program_course.id, programCourseId));
}

// ─── Cohortes ────────────────────────────────────────────────────────────────

export interface CreateCohortInput {
  organizationId: string;
  courseId: string;
  programId?: string | null;
  campusId?: string | null;
  code?: string;
  name?: string;
  modality?: "in_person" | "online_live" | "hybrid" | "self_paced";
  startDate?: string | null;
  endDate?: string | null;
  capacity?: number;
  enrollmentRule?: "open" | "approval" | "waitlist";
  status?: "draft" | "open" | "running" | "completed" | "cancelled";
  gradingScheme?: string | null;
}

export async function createCohortMutation(
  db: PowerSyncDrizzleDB,
  input: CreateCohortInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(cohort).values({
    id,
    organization_id: input.organizationId,
    course_id: input.courseId,
    program_id: input.programId ?? null,
    campus_id: input.campusId ?? null,
    code: input.code ?? null,
    name: input.name?.trim() || "Nueva cohorte",
    // §L2: online en vivo de fábrica (espejo del DEFAULT de `cohort.modality`
    // en la migración 00083). El camino sin decisiones lleva a lo que la
    // academia vende: cursos online.
    modality: input.modality ?? DEFAULT_COHORT_MODALITY,
    start_date: input.startDate ?? null,
    end_date: input.endDate ?? null,
    capacity: input.capacity ?? 0,
    enrollment_rule: input.enrollmentRule ?? "open",
    status: input.status ?? "draft",
    grading_scheme: input.gradingScheme ?? null,
    config: "{}",
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateCohortMutation(
  db: PowerSyncDrizzleDB,
  cohortId: string,
  updates: Partial<{
    name: string;
    code: string | null;
    modality: string;
    start_date: string | null;
    end_date: string | null;
    capacity: number;
    enrollment_rule: string;
    status: string;
    grading_scheme: string | null;
    grading_model_id: string | null;
    campus_id: string | null;
    program_id: string | null;
  }>,
): Promise<void> {
  await db
    .update(cohort)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(cohort.id, cohortId));
}

export async function deleteCohortMutation(
  db: PowerSyncDrizzleDB,
  cohortId: string,
): Promise<void> {
  await db
    .update(cohort)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(cohort.id, cohortId));
}

// ─── Profesores ──────────────────────────────────────────────────────────────

export async function addCohortTeacherMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId: string;
    userId: string;
    role?: "lead" | "assistant";
  },
): Promise<string> {
  const id = generateUUID();
  await db.insert(cohort_teacher).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    role: params.role ?? "lead",
    created_at: nowISO(),
  });
  return id;
}

export async function removeCohortTeacherMutation(
  db: PowerSyncDrizzleDB,
  cohortTeacherId: string,
): Promise<void> {
  await db.delete(cohort_teacher).where(eq(cohort_teacher.id, cohortTeacherId));
}

// ─── Matrícula ───────────────────────────────────────────────────────────────

/**
 * El ALUMNO solicita matrícula local-first (regla 'open'): crea su fila
 * `pre_enrolled` — el write-path valida regla/estado de la cohorte y un
 * manager (o el endpoint REST) la activa con control de cupo.
 */
export async function requestCohortEnrollmentMutation(
  db: PowerSyncDrizzleDB,
  params: { cohortId: string; organizationId: string; campusId?: string | null; userId: string },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(cohort_enrollment).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    campus_id: params.campusId ?? null,
    user_id: params.userId,
    status: "pre_enrolled",
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Propone una TRANSICIÓN de estado (activar/retirar/completar/…). El servidor
 * valida la transición y ejecuta los efectos (cupo, enrollment de contenido,
 * promoción de waitlist); un cambio inválido simplemente no se aplica.
 */
export async function transitionCohortEnrollmentMutation(
  db: PowerSyncDrizzleDB,
  enrollmentId: string,
  status: "pre_enrolled" | "waitlisted" | "active" | "completed" | "withdrawn",
): Promise<void> {
  await db
    .update(cohort_enrollment)
    .set({ status, updated_at: nowISO() })
    .where(eq(cohort_enrollment.id, enrollmentId));
}

/** Notas/nota final del expediente de matrícula (solo managers, valida server). */
export async function updateCohortEnrollmentMutation(
  db: PowerSyncDrizzleDB,
  enrollmentId: string,
  updates: Partial<{
    notes: string | null;
    final_grade: number | null;
    final_grade_scheme: string | null;
  }>,
): Promise<void> {
  await db
    .update(cohort_enrollment)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(cohort_enrollment.id, enrollmentId));
}

// ─── Grupos ──────────────────────────────────────────────────────────────────

export async function createCohortGroupMutation(
  db: PowerSyncDrizzleDB,
  params: { cohortId: string; organizationId: string; name?: string },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(cohort_group).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    name: params.name?.trim() || "Nuevo grupo",
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteCohortGroupMutation(
  db: PowerSyncDrizzleDB,
  groupId: string,
): Promise<void> {
  await db
    .update(cohort_group)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(cohort_group.id, groupId));
}

export async function addCohortGroupMemberMutation(
  db: PowerSyncDrizzleDB,
  params: { groupId: string; cohortId: string; organizationId: string; userId: string },
): Promise<string> {
  const id = generateUUID();
  await db.insert(cohort_group_member).values({
    id,
    group_id: params.groupId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    created_at: nowISO(),
  });
  return id;
}

export async function removeCohortGroupMemberMutation(
  db: PowerSyncDrizzleDB,
  memberId: string,
): Promise<void> {
  await db.delete(cohort_group_member).where(eq(cohort_group_member.id, memberId));
}

// ─── Ajustes por módulo y cohorte ────────────────────────────────────────────

/**
 * Upsert local del ajuste (cohort_id, module_id): due/open/visibilidad.
 * PowerSync no admite UPSERT sobre vistas → UPDATE-o-INSERT manual.
 */
export async function upsertCohortModuleSettingMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    moduleId: string;
    organizationId: string;
    dueAt?: string | null;
    openAt?: string | null;
    visibility?: "visible" | "hidden" | null;
    config?: unknown;
  },
): Promise<string> {
  const now = nowISO();
  const rows = await db
    .select({ id: cohort_module_setting.id, module_id: cohort_module_setting.module_id })
    .from(cohort_module_setting)
    .where(eq(cohort_module_setting.cohort_id, params.cohortId));
  const found = rows.find((r) => r.module_id === params.moduleId);
  const values: Record<string, unknown> = {
    due_at: params.dueAt ?? null,
    open_at: params.openAt ?? null,
    visibility: params.visibility ?? null,
    config: JSON.stringify(params.config ?? {}),
    updated_at: now,
  };
  if (found) {
    await db
      .update(cohort_module_setting)
      .set(values)
      .where(eq(cohort_module_setting.id, found.id));
    return found.id;
  }
  const id = generateUUID();
  await db.insert(cohort_module_setting).values({
    id,
    cohort_id: params.cohortId,
    module_id: params.moduleId,
    organization_id: params.organizationId,
    created_at: now,
    ...values,
  });
  return id;
}
